import React, { useState, forwardRef } from 'react';
import { brand, shadow, radius } from './tokens';

// GastroKIQ Design System v1 — KIQButton. SOLO el componente visual
// reutilizable (sección 16 del pedido): no conecta IA, no dispara ningún
// request, no es todavía el botón flotante global — eso queda para una
// fase posterior. Aquí solo se preparan sus 5 estados visuales y su forma
// de recibir notificaciones.
//
// Estados: idle / hover (interno, vía mouse) / notification / analyzing /
// disabled. "analyzing" reutiliza la MISMA animación `pulse` que ya existe
// en public/index.html (usada hoy por PantallaCarga en App.js) — ninguna
// keyframe nueva hizo falta para ese estado, ya existía la sensación visual
// correcta ("respirando") para comunicar "procesando".
//
// `ariaExpanded` (Fase 3 — KIQ Assistant Panel): prop opcional, aditiva.
// Cuando este botón controla un panel abrible (el caso real desde Fase 3),
// quien lo use pasa `ariaExpanded={panelAbierto}` para exponer el estado de
// disclosure correcto — nunca cambia la identidad visual del botón. Se deja
// `undefined` por defecto (se omite el atributo) para no alterar ningún uso
// existente que no controle un panel.
// `className` (Fase 3.4.10, botón flotante adaptativo): aditivo, '' por
// defecto — se agrega DESPUÉS de 'kiq-focusable' (nunca la reemplaza), así
// que los 2 usos existentes que no la pasan (DesignSystem.js) quedan
// pixel-idénticos. Layout.js la usa para una regla de
// prefers-reduced-motion acotada a su propio botón flotante (ver ese
// archivo) sin tocar este componente compartido más de lo necesario.
// `ref` (Fase 3.4.11-R): forwardRef aditivo — permite que Layout.js maneje
// un handoff de foco predecible cuando este botón se desmonta a favor del
// trigger de respaldo del header (y viceversa), sin romper ningún uso
// existente que no pase una ref (React ignora un forwardRef no usado por
// el caller, DesignSystem.js sigue pixel-idéntico).
const KIQButton = forwardRef(function KIQButton({ state = 'idle', notificationCount = 0, label = 'KIQ', onClick, size = 44, ariaExpanded, className = '' }, ref) {
  const [hover, setHover] = useState(false);
  const disabled = state === 'disabled';
  const analyzing = state === 'analyzing';
  const notificacion = state === 'notification' && notificationCount > 0;
  // Cuadrado redondeado (la forma real de la marca, kiq-mark.png) en vez de
  // círculo — pedido explícito tras ver el botón en vivo. Misma proporción
  // que ya pedía el README (19px de radio para 58px, 16px para 48px ≈ 33%
  // del tamaño) — nunca radius.pill (círculo completo).
  const radioBoton = Math.round(size * 0.33);

  const ariaLabel = analyzing
    ? `${label} · analizando`
    : notificacion
      ? `${label} · ${notificationCount} ${notificationCount === 1 ? 'notificación' : 'notificaciones'}`
      : label;

  return (
    <button
      ref={ref}
      type="button"
      className={className ? `kiq-focusable ${className}` : 'kiq-focusable'}
      aria-label={ariaLabel}
      aria-busy={analyzing || undefined}
      aria-expanded={ariaExpanded}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        width: size, height: size, flexShrink: 0,
        borderRadius: `${radioBoton}px`,
        border: '1px solid rgba(255,255,255,.18)',
        background: brand.gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        boxShadow: hover && !disabled ? shadow.lg : shadow.kiq,
        transform: hover && !disabled && !analyzing ? 'translateY(-1px)' : 'translateY(0)',
        animation: analyzing ? 'pulse 1.5s infinite' : 'none',
        // Fase 3.4.10 — width/height sumados a la transición existente para
        // que el cambio compacto<->normal (Layout.js) se sienta como un
        // ajuste subtil, nunca un salto instantáneo; el propio caller
        // (Layout.js) neutraliza esto bajo prefers-reduced-motion vía la
        // clase `className` de arriba, sin tocar box-shadow/transform/
        // opacity (que siempre existieron aquí, sin relación con el tamaño).
        transition: 'box-shadow 160ms ease, transform 160ms ease, opacity 160ms ease, width 200ms ease, height 200ms ease',
      }}
    >
      {/* Anillo pulsante permanente (kiqpulse, ya definido en
          public/index.html — misma animación que ya usan el logo del sidebar
          en otras pantallas y el mark del banner KIQ) — puramente decorativo,
          nunca intercepta clicks, y se detiene si el botón está disabled. */}
      {!disabled && (
        <span aria-hidden="true" style={{ position: 'absolute', inset: '-5px', borderRadius: `${radioBoton + 4}px`, border: '2px solid rgba(255,45,138,.5)', animation: 'kiqpulse 2.6s ease-out infinite', pointerEvents: 'none' }} />
      )}
      {/* Marca real de KIQ (misma imagen que ya usan los banners del resto de
          la app) — nunca el placeholder SVG de KIQMark.js. Proporción ~0.72
          del tamaño del botón, igual que el botón flotante de referencia
          (Shell App.dc.html: 42px de imagen dentro de un botón de 58px). */}
      <img src="/kiq-mark.png" alt="" style={{ width: Math.round(size * 0.72), height: Math.round(size * 0.72), borderRadius: '22%', objectFit: 'cover', display: 'flex' }} onError={(e) => { e.currentTarget.style.display = 'none'; }} />

      {notificacion && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', top: -2, right: -2, minWidth: '16px', height: '16px', padding: '0 3px',
            borderRadius: radius.pill, background: '#FF2D78', color: '#fff',
            fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid var(--bg)',
          }}
        >
          {notificationCount > 9 ? '9+' : notificationCount}
        </span>
      )}
    </button>
  );
});

export default KIQButton;
